/* eslint-disable react-hooks/set-state-in-effect -- fetch-on-mount via useCallback (load), pattern standard */
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { bonVisiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { cn } from '../../lib/utils';
import {
    FileSignature, Loader2, X, Plus, Printer, Copy, Check, Trash2,
    CheckCircle2, Clock, ChevronDown, ChevronUp, QrCode
} from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
};

function CreateSlipModal({ open, onClose, mandateId, onCreated }) {
    const today = new Date().toISOString().slice(0, 10);
    const [buyer, setBuyer] = useState({ first_name: '', last_name: '', email: '', phone: '' });
    const [visitDate, setVisitDate] = useState(today);
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setBuyer(p => ({ ...p, [k]: v }));

    if (!open) return null;
    const valid = buyer.first_name.trim() && buyer.last_name.trim() && visitDate;

    const create = async () => {
        if (!valid) return;
        setSaving(true);
        try {
            const res = await bonVisiteAPI.create({
                mandate_id: mandateId,
                visit_date: visitDate,
                buyers: [{ ...buyer, email: buyer.email || null, phone: buyer.phone || null }],
            });
            toast.success('Bon de visite généré');
            onCreated?.(res.data);
            setBuyer({ first_name: '', last_name: '', email: '', phone: '' });
            onClose();
        } catch {
            toast.error('Échec de la génération');
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="create-slip-modal">
            <div className="w-full max-w-md rounded-2xl bg-[#15101d] border border-white/10 shadow-2xl">
                <div className="flex items-center justify-between p-5 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <FileSignature className="w-5 h-5 text-[#FF914D]" />
                        <h3 className="font-bold text-white">Nouveau bon de visite</h3>
                    </div>
                    <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="create-slip-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <p className="text-xs text-muted-foreground">Document anonymisé (sans adresse ni nom du vendeur) attestant la visite via l&apos;agence.</p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1 block">Prénom acquéreur *</label>
                            <input value={buyer.first_name} onChange={e => set('first_name', e.target.value)}
                                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF914D]/50" data-testid="slip-buyer-first" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1 block">Nom acquéreur *</label>
                            <input value={buyer.last_name} onChange={e => set('last_name', e.target.value)}
                                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF914D]/50" data-testid="slip-buyer-last" />
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1 block">Date de visite *</label>
                        <input type="date" value={visitDate} onChange={e => setVisitDate(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF914D]/50" data-testid="slip-visit-date" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 p-5 border-t border-white/10">
                    <Button variant="ghost" onClick={onClose} disabled={saving}>Annuler</Button>
                    <Button onClick={create} disabled={saving || !valid}
                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold" data-testid="create-slip-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
                        Générer
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function SlipCard({ slip, onDeleted }) {
    const [copied, setCopied] = useState(false);
    const [showQr, setShowQr] = useState(slip.status !== 'signed');
    const signUrl = `${window.location.origin}/bon-visite/${slip.signing_token}`;
    const buyerNames = (slip.buyers || []).map(b => `${b.first_name} ${b.last_name}`).join(', ');
    const signed = slip.status === 'signed';

    const copy = () => {
        navigator.clipboard.writeText(signUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const del = async () => {
        try {
            await bonVisiteAPI.remove(slip.id);
            toast.success('Bon de visite supprimé');
            onDeleted?.();
        } catch {
            toast.error('Suppression impossible');
        }
    };

    return (
        <Card className="p-3 border bg-white/[0.03] border-white/5" data-testid={`visit-slip-${slip.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm text-white truncate">{buyerNames || 'Acquéreur'}</p>
                        <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border",
                            signed ? "bg-green-500/15 text-green-400 border-green-500/30" : "bg-yellow-500/15 text-yellow-400 border-yellow-500/30")}>
                            {signed ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                            {signed ? 'Signé' : 'En attente'}
                        </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                        Réf. {slip.property_ref?.reference} · {slip.property_ref?.type} · visite du {fmtDate(slip.visit_date)}
                    </p>
                    {signed && <p className="text-[11px] text-green-400/80 mt-0.5">Signé par {slip.signed_full_name} le {fmtDate(slip.signed_at)}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-muted-foreground hover:text-white"
                        onClick={() => window.open(bonVisiteAPI.pdfUrl(slip.id), '_blank')}
                        title="Imprimer / Télécharger" data-testid={`slip-print-${slip.id}`}>
                        <Printer className="w-4 h-4" />
                    </Button>
                    {!signed && (
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-muted-foreground hover:text-red-400"
                            onClick={del} title="Supprimer" data-testid={`slip-delete-${slip.id}`}>
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    )}
                </div>
            </div>

            {!signed && (
                <div className="mt-3">
                    <button onClick={() => setShowQr(v => !v)} className="flex items-center gap-1.5 text-[11px] text-[#FF914D] font-medium" data-testid={`slip-toggle-qr-${slip.id}`}>
                        <QrCode className="w-3.5 h-3.5" />
                        Signature électronique sur place
                        {showQr ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    {showQr && (
                        <div className="flex flex-col sm:flex-row items-center gap-3 mt-2 p-3 rounded-xl bg-white/5 border border-white/10">
                            <div className="bg-white p-2 rounded-lg shrink-0">
                                <QRCodeSVG value={signUrl} size={96} level="M" />
                            </div>
                            <div className="flex-1 w-full min-w-0">
                                <p className="text-[11px] text-muted-foreground mb-2">L&apos;acquéreur scanne et signe depuis son téléphone, ou imprimez pour une signature manuscrite.</p>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 text-[10px] px-2 py-1.5 rounded-lg bg-white/5 text-white/50 truncate">{signUrl}</code>
                                    <Button size="sm" variant="outline" onClick={copy} className="h-8 px-3 shrink-0" data-testid={`slip-copy-${slip.id}`}>
                                        {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
}

export function BonVisiteSection({ mandateId }) {
    const [slips, setSlips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await bonVisiteAPI.listByMandate(mandateId);
            setSlips(res.data.slips || []);
        } catch {
            /* noop */
        } finally {
            setLoading(false);
        }
    }, [mandateId]);

    useEffect(() => { load(); }, [load]);

    const signedCount = slips.filter(s => s.status === 'signed').length;

    return (
        <div className="space-y-3 pt-2 border-t border-white/5">
            <button onClick={() => setExpanded(v => !v)} className="w-full flex items-center justify-between" data-testid="bon-visite-toggle">
                <div className="flex items-center gap-2">
                    <FileSignature className="w-5 h-5 text-[#681793]" />
                    <h3 className="font-bold text-sm text-white">Bons de visite</h3>
                    {slips.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-[#681793]/15 text-[#b07bd6] text-[10px] font-bold">
                            {slips.length}{signedCount > 0 ? ` · ${signedCount} signé${signedCount > 1 ? 's' : ''}` : ''}
                        </span>
                    )}
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>

            {expanded && (
                <>
                    <Button onClick={() => setModalOpen(true)} size="sm"
                        className="w-full bg-white/5 hover:bg-white/10 text-white border border-white/10" data-testid="create-slip-btn">
                        <Plus className="w-4 h-4 mr-1.5" />
                        Générer un bon de visite
                    </Button>

                    {loading ? (
                        <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                    ) : slips.length === 0 ? (
                        <p className="text-center text-xs text-muted-foreground py-3">
                            Aucun bon de visite. Générez-en un pour faire signer l&apos;acquéreur (sur place ou par impression).
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {slips.map(s => <SlipCard key={s.id} slip={s} onDeleted={load} />)}
                        </div>
                    )}
                </>
            )}

            <CreateSlipModal open={modalOpen} onClose={() => setModalOpen(false)} mandateId={mandateId} onCreated={load} />
        </div>
    );
}
